<!-- Page Search Template (injected into center zone of toolbar) -->
<ng-template #toolbarSearch>
  <!-- Desktop/tablet (md+): plain search input, no scanner button -->
  <div class="hidden md:block w-full">
    <ui-input
      type="search"
      [value]="searchTerm"
      (valueChange)="onSearch($event)"
      placeholder="Search products..."
      [clearable]="true"
      (cleared)="clearSearch()"
      [fullWidth]="true"
      ariaLabel="Search products">
    </ui-input>
  </div>

  <!-- Mobile (<md): join-style input + scanner button -->
  <div class="md:hidden join w-full flex items-stretch" >
    <ui-join-input
      class="flex-1 min-w-0"
      type="search"
      [value]="searchTerm"
      (valueChange)="onSearch($event)"
      placeholder="Search products..."
      [clearable]="true"
      (cleared)="clearSearch()"
      ariaLabel="Search products">
    </ui-join-input>

    <ui-button
      class="join-item shrink-0"
      variant="secondary"
      [tone]="'solid'"
      [size]="'md'"
      [className]="'ms-3'"
      (clicked)="openScanner()">
      <fa-icon [icon]="faScan"></fa-icon>
    </ui-button>
  </div>

  <!-- Scanner modal (kept outside the join layout so it doesn't affect the
       flex sizing of the search row). -->
  <ui-modal
    #scannerModal
    title="Barcode Scanner"
    size="full"
    [showCloseButton]="true"
    [closeOnBackdrop]="true"
    (modalClosed)="onScannerClosed()">

    <app-scanner
      #scanner
      [sound]="true"
      (codeAccepted)="onScannerCodeAccepted($event)">
    </app-scanner>

    <div modal-footer class="flex justify-end gap-2">
      <ui-button variant="ghost" type="button" (clicked)="scannerModal.close()">Close</ui-button>
    </div>
  </ui-modal>
</ng-template>

<!-- Page Actions are registered declaratively in the component
     (see refreshPageActions in products.component.ts). -->

@if (products) {
  <div class="mb-4 flex flex-wrap items-center gap-3">
    <!-- Stock filter pills -->
    <div class="grow">
      <ui-button-group [fullWidthMobile]="true">
        <ui-button
          [variant]="selectedFilter === 'all' ? 'primary' : 'ghost'"
          size="sm"
          (clicked)="setFilter('all')">
          All
        </ui-button>
        <ui-button
          [variant]="selectedFilter === 'inStock' ? 'primary' : 'ghost'"
          size="sm"
          (clicked)="setFilter('inStock')">
          In Stock
        </ui-button>
        <ui-button
          [variant]="selectedFilter === 'lowStock' ? 'primary' : 'ghost'"
          size="sm"
          (clicked)="setFilter('lowStock')">
          Low Stock
        </ui-button>
        <ui-button
          [variant]="selectedFilter === 'outOfStock' ? 'primary' : 'ghost'"
          size="sm"
          (clicked)="setFilter('outOfStock')">
          Out of Stock
        </ui-button>
      </ui-button-group>
    </div>

    <!-- Right cluster: Product Kind dropdown + Category filter modal trigger -->
    <div class="flex items-center gap-2 w-full sm:w-auto sm:ms-auto">
      <div class="flex-1 sm:flex-none sm:w-56">
        <ui-autocomplete
          size="sm"
          [fullWidth]="true"
          [options]="productKindAutocompleteOptions"
          [value]="selectedProductKey"
          (valueChange)="setProductKindFilter($event)"
          placeholder="Product kind…"
          emptyMessage="No matching product kinds"
          ariaLabel="Filter by product kind">
        </ui-autocomplete>
      </div>

      <ui-button
        size="sm"
        [variant]="selectedCategoryKey ? 'primary' : 'ghost'"
        (clicked)="openCategoryFilter()"
        [ariaLabel]="'Filter by category'">
        <fa-icon [icon]="faFilter"></fa-icon>
        @if (selectedCategoryKey) {
          <span class="ml-1 truncate max-w-32">{{ selectedCategoryLabel }}</span>
        }
      </ui-button>

      @if (hasActiveSubFilters) {
        <ui-button variant="ghost" size="sm" (clicked)="clearSubFilters()">
          Clear
        </ui-button>
      }
    </div>
  </div>

  <!-- Category filter modal -->
  <ui-modal
    #categoryFilterModal
    title="Filter by Category"
    size="md"
    [showCloseButton]="true"
    [closeOnBackdrop]="true">

    <div class="space-y-4">
      <!-- "All" tile to clear the category filter -->
      <button
        type="button"
        class="w-full p-3 rounded-lg border text-left transition-colors"
        [class.border-primary]="!selectedCategoryKey"
        [class.bg-primary]="!selectedCategoryKey"
        [class.text-primary-content]="!selectedCategoryKey"
        [class.border-base-300]="selectedCategoryKey"
        [class.hover:bg-base-200]="selectedCategoryKey"
        (click)="selectCategoryFromModal('')">
        <span class="font-medium">All categories</span>
      </button>

      @if (categories?.length) {
        <div class="grid grid-cols-2 gap-2">
          @for (cat of categories; track cat.key) {
            <button
              type="button"
              class="p-3 rounded-lg border text-left transition-colors min-h-16"
              [class.border-primary]="selectedCategoryKey === cat.key"
              [class.bg-primary]="selectedCategoryKey === cat.key"
              [class.text-primary-content]="selectedCategoryKey === cat.key"
              [class.border-base-300]="selectedCategoryKey !== cat.key"
              [class.hover:bg-base-200]="selectedCategoryKey !== cat.key"
              (click)="selectCategoryFromModal(cat.key)">
              <span class="font-medium block truncate">{{ cat.name_de || cat.key }}</span>
              <span
                class="text-xs font-mono truncate block"
                [class.opacity-80]="selectedCategoryKey === cat.key"
                [class.text-base-content]="selectedCategoryKey !== cat.key"
                [class.opacity-60]="selectedCategoryKey !== cat.key">
                {{ cat.key }}
              </span>
            </button>
          }
        </div>
      } @else {
        <div class="text-center py-6 text-base-content/60 text-sm">
          No categories available.
        </div>
      }
    </div>

    <div modal-footer class="flex justify-end">
      <ui-button variant="ghost" (clicked)="closeCategoryFilter()">Close</ui-button>
    </div>
  </ui-modal>
}

@if (products) {
  <div class="mb-5">
    @if (filteredProducts?.length) {
      <ui-list variant="bordered">
        @for (p of pagedProducts; track trackByProductId($index, p)) {
          <ui-list-item [clickable]="true">
            <a [routerLink]="['/products', p.id]" class="flex items-center w-full gap-3 text-inherit no-underline hover:no-underline">
              <!-- Product thumbnail -->
              <div class="shrink-0">
                <div class="w-12 h-12 relative overflow-hidden rounded-lg bg-base-200">
                  @if (p.primary_thumb_image?.url) {
                    <img
                      [src]="p.primary_thumb_image?.url"
                      class="absolute inset-0 m-auto max-w-full max-h-full object-contain"
                      alt=""/>
                  } @else {
                    <div class="absolute inset-0 flex items-center justify-center text-base-content/40">
                      <fa-icon [icon]="faProductPlaceholder" size="lg"></fa-icon>
                    </div>
                  }
                </div>
              </div>

              <!-- Product info - takes remaining space -->
              <div class="flex-1 min-w-0 flex items-center gap-3">
                <!-- Name and category -->
                <div class="flex-1 min-w-0">
                  @if (p.name) {
                    <div class="font-medium text-base-content truncate">{{ p | productName }}</div>
                  } @else {
                    <div class="font-medium text-base-content/40 truncate italic">Not Set</div>
                  }
                </div>

                <!-- Category and quantity - hidden on mobile -->
                <div class="hidden sm:flex items-center gap-3 shrink-0">
                  @if (p.classification?.category?.name_de) {
                    <span class="text-sm text-base-content/60 truncate max-w-30">{{ p.classification.category.name_de }}</span>
                  }
                  @if (p.quantity_type === 'variable') {
                    <span class="badge badge-info badge-sm text-xs">Variable</span>
                  }
                  @if (p.quantity_as_text) {
                    <span class="badge badge-outline badge-sm text-xs">{{ p.quantity_as_text }}</span>
                  }
                </div>
              </div>

              <!-- Stock badge - always visible -->
              <div class="shrink-0">
                <div
                  class="inline-flex items-center justify-center min-w-10 h-9 px-3 rounded-lg font-bold text-base shadow-sm"
                  [ngClass]="{
                    'bg-success text-success-content': (p.stock?.available ?? 0) > 0 && (p.stock?.missing ?? 0) === 0,
                    'bg-warning text-warning-content': (p.stock?.available ?? 0) > 0 && (p.stock?.missing ?? 0) > 0,
                    'bg-error text-error-content': (p.stock?.available ?? 0) === 0 && (p.stock?.missing ?? 0) > 0,
                    'bg-base-300 text-base-content': (p.stock?.available ?? 0) === 0 && (p.stock?.missing ?? 0) === 0
                  }">
                  {{ p.stock?.available ?? 0 }}
                </div>
              </div>
            </a>
          </ui-list-item>
        }
      </ui-list>

      <!-- Pagination -->
      @if (totalItems > pageSize) {
        <div class="mt-4">
          <ui-pagination
            [currentPage]="currentPage"
            [totalPages]="totalPages"
            [totalItems]="totalItems"
            [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions"
            (pageChange)="onPageChange($event)"
            (pageSizeChange)="onPageSizeChange($event)">
          </ui-pagination>
        </div>
      }
    } @else {
      <div class="text-center py-8 text-base-content/60">
        No products found
      </div>
    }
  </div>
} @else {
  <div class="flex justify-center items-center min-h-[30vh]">
    <div class="flex items-center gap-3">
      <span class="loading loading-spinner loading-md"></span>
      <strong>Loading...</strong>
    </div>
  </div>
}
